// Utility functions for price adjustments in quotations

const GST_PERCENT = 5;

/**
 * Calculate adjusted pricing after applying a price adjustment
 * @param {Object} originalDerived - Original derived pricing from priceQuote()
 * @param {Number} adjustmentAmount - Amount to add (positive) or subtract (negative)
 * @returns {Object} Adjusted pricing object
 */
export function calculateAdjustedPricing(originalDerived, adjustmentAmount) {
  if (!originalDerived || adjustmentAmount === 0) {
    return originalDerived;
  }

  // Apply adjustment to base price
  const adjustedBasePrice = originalDerived.basePrice + adjustmentAmount;
  
  // Ensure base price doesn't go negative
  if (adjustedBasePrice < 0) {
    throw new Error("Adjusted base price cannot be negative");
  }

  // Recalculate GST based on adjusted base price + logistics
  const logisticsBase = originalDerived.logisticsBase || originalDerived.logisticsInclusive || 0;
  const systemTotal = adjustedBasePrice + logisticsBase;
  const systemTotal70Percent = systemTotal * 0.7;
  const systemTotal30Percent = systemTotal * 0.3;
  const gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
  const gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
  const gstAmount = gst5Percent + gst18Percent;

  // Calculate total before subsidy
  const totalBeforeSubsidy = systemTotal + gstAmount;

  // Calculate final payable (considering subsidy if applicable)
  let totalPayable = totalBeforeSubsidy;
  if (originalDerived.subsidyApplicable && originalDerived.subsidyAmount > 0) {
    totalPayable = Math.max(0, totalBeforeSubsidy - originalDerived.subsidyAmount);
  }

  return {
    ...originalDerived,
    basePrice: adjustedBasePrice,
    gstAmount: gstAmount,
    gst5Percent: gst5Percent,
    gst18Percent: gst18Percent,
    totalBeforeSubsidy: totalBeforeSubsidy,
    totalPayable: totalPayable,
    priceAdjustment: adjustmentAmount,
    originalBasePrice: originalDerived.basePrice // Keep original for reference
  };
}

/**
 * Get the final amount for payment link
 * Uses totalBeforeSubsidy (before subsidy) as per requirements
 * @param {Object} derived - Derived pricing (may include adjustments)
 * @returns {Number} Final amount in rupees
 */
export function getPaymentLinkAmount(derived) {
  if (!derived) return 0;
  // Use totalBeforeSubsidy (before subsidy) as per requirements
  return derived.totalBeforeSubsidy || 0;
}
